import { normalizeColorHex } from '@/lib/utils';
import { Currency } from '@/modules/common';

import {
  BillHoursSourceType,
  BillTimeOffSourceType,
  GenerateBillListItem,
  GenerateBillMemberProjectListItem,
  GenerateBillMemberTimeOffListItem,
  GenerateBillSubRowType,
} from '../types';
import {
  BillTimeOffTypeSourceResponse,
  GeneratedMemberBillProjectItemResponse,
  GeneratedMemberBillResponse,
  GetGeneratedBillsResponse,
} from '../types/dto/response';
import {
  mapBillHoursSourcesFromDTO,
  mapBillTimeOffSourcesFromDTO,
} from './bill-hours-source.mapper';

const mapProjectFromResponse = (
  projectResponse: GeneratedMemberBillProjectItemResponse,
  currencyCode: string
): GenerateBillMemberProjectListItem => {
  const hoursSources = mapBillHoursSourcesFromDTO(projectResponse.hoursSources);

  const primarySource =
    hoursSources.find(s => s.sourceType === BillHoursSourceType.TRACKED) || hoursSources[0];

  return {
    type: GenerateBillSubRowType.PROJECT,
    id: projectResponse.project.id.toString(),
    name: projectResponse.project.name,
    clientName: projectResponse.clientName,
    logoUrl: projectResponse.project.logoUrl,
    colorHex: normalizeColorHex(projectResponse.project.colorHex) ?? '',
    billBy: primarySource?.sourceType || BillHoursSourceType.TRACKED,
    hours: primarySource?.hours || 0,
    effectiveRate: primarySource?.averageRate || 0,
    currency: {
      code: currencyCode,
      displayName: '',
    } as Currency,
    total: primarySource?.amount || 0,
    hoursSources,
    warnings: projectResponse.warnings,
  };
};

const mapTimeOffFromResponse = (
  timeOff: BillTimeOffTypeSourceResponse,
  currencyCode: string
): GenerateBillMemberTimeOffListItem => {
  const hoursSources = mapBillTimeOffSourcesFromDTO(timeOff.hoursSources);

  // Default to APPROVED so a bill never silently includes unapproved time-off. Falls back to
  // whatever single source exists (a PENDING-only member), then to the flat server figures for
  // resilience if the backend ever returns no sources at all.
  const primarySource =
    hoursSources.find(s => s.sourceType === BillTimeOffSourceType.APPROVED) ?? hoursSources[0];

  return {
    type: GenerateBillSubRowType.TIME_OFF,
    id: `timeoff-${timeOff.timeOffTypeId}`,
    timeOffTypeId: timeOff.timeOffTypeId,
    timeOffTypeName: timeOff.timeOffTypeName,
    isBillable: timeOff.isBillable,
    billBy: primarySource?.sourceType ?? BillTimeOffSourceType.APPROVED,
    hours: primarySource?.hours ?? parseFloat(timeOff.hours),
    effectiveRate: primarySource?.averageRate ?? parseFloat(timeOff.averageRate),
    currency: {
      code: currencyCode,
      displayName: '',
    } as Currency,
    total: primarySource?.amount ?? parseFloat(timeOff.amount),
    hoursSources,
  };
};

const mapMemberBillFromResponse = (
  memberResponse: GeneratedMemberBillResponse
): GenerateBillListItem => {
  const memberProjects = memberResponse.projects.map(project =>
    mapProjectFromResponse(project, memberResponse.currencyCode)
  );

  const memberTimeOffs = (memberResponse.timeOffSources ?? []).map(timeOff =>
    mapTimeOffFromResponse(timeOff, memberResponse.currencyCode)
  );

  const memberSubRows = [...memberProjects, ...memberTimeOffs];

  const totalHours = memberProjects.reduce((sum, project) => sum + project.hours, 0);
  const totalAmount = memberProjects.reduce((sum, project) => sum + project.total, 0);
  const avgRate = totalHours > 0 ? totalAmount / totalHours : 0;

  return {
    id: memberResponse.member.id.toString(),
    member: {
      id: memberResponse.member.id,
      name: memberResponse.member.name,
      email: memberResponse.member.email,
      avatarUrl: memberResponse.member.profileImageUrl,
    },
    hours: totalHours,
    effectiveRate: avgRate,
    currency: {
      code: memberResponse.currencyCode,
      displayName: '',
    },
    currencyCode: memberResponse.currencyCode,
    total: totalAmount,
    warnings: memberResponse.warnings,
    memberProjects,
    memberAddedProjects: [],
    memberTimeOffs,
    memberOverheads: [],
    memberSubRows,
    canSelectMonthlySalary: memberResponse.canSelectMonthlySalary ?? false,
    monthlySalaryAmount: parseFloat(memberResponse.monthlySalaryAmount ?? '0') || 0,
    isMonthlySalaryEnabled: false,
    memberSalaryAdjustment: null,
  };
};

export const mapGeneratedBillsFromResponse = (
  response: GetGeneratedBillsResponse
): GenerateBillListItem[] => {
  return response.members.map(mapMemberBillFromResponse);
};
